import type { FileItem } from "../types/file-item.types.ts"
import type { FilesSortOrder } from "../types/files-filters.types.ts"
import type { FolderListItem } from "../types/folder.types.ts"
import { readDocumentDay } from "./document-date.ts"
import { folderItemCount } from "./folder-item-count.ts"
import { type FileDateAccount, readFileDate, uploadDayOf } from "./read-file-date.ts"

interface SortKeys {
  name: string
  created: string
  updated: string
  size: number
  documentDate?: string
}

interface Keyed<T> {
  item: T
  name: string
  created: number
  updated: number
  paperDate: number
  size: number
}

type Compare = <T>(a: Keyed<T>, b: Keyed<T>) => number

const newestModified: Compare = (a, b) => b.updated - a.updated

const compareBy = (order: FilesSortOrder): Compare => {
  switch (order) {
    case "added_desc":
      return (a, b) => b.created - a.created
    case "document_date_desc":
      return (a, b) => b.paperDate - a.paperDate
    case "document_date_asc":
      return (a, b) => a.paperDate - b.paperDate
    case "name_asc":
      return (a, b) => a.name.localeCompare(b.name)
    case "name_desc":
      return (a, b) => b.name.localeCompare(a.name)
    case "size_desc":
      return (a, b) => b.size - a.size
    case "size_asc":
      return (a, b) => a.size - b.size
    case "modified_desc":
    default:
      return newestModified
  }
}

/** A tie goes to the most recently modified, then keeps the input order: for items
 * rendered to the same minute, that order is the only record of which came last. */
const sortBy = <T>(
  items: T[],
  order: FilesSortOrder,
  account: FileDateAccount,
  keysOf: (item: T) => SortKeys
): T[] => {
  const compare = compareBy(order)
  const instantOf = (value: string) => readFileDate(value, account) ?? 0
  return items
    .map((item) => {
      const keys = keysOf(item)
      const created = instantOf(keys.created)
      return {
        item,
        name: keys.name,
        created,
        updated: instantOf(keys.updated),
        paperDate: readDocumentDay(keys.documentDate) ?? uploadDayOf(created, account.timezone),
        size: keys.size
      }
    })
    .sort((a, b) => compare(a, b) || newestModified(a, b))
    .map(({ item }) => item)
}

/** Client-side because album files and documents come from two endpoints. */
export const sortFileItems = (
  items: FileItem[],
  order: FilesSortOrder,
  account: FileDateAccount
): FileItem[] =>
  sortBy(items, order, account, (item) => ({
    name: item.name,
    created: item.created,
    updated: item.updated,
    size: item.fileSize ?? 0,
    documentDate: item.documentDate
  }))

/** A folder has no byte size, so the size orders read the item count its card shows. */
export const sortFolders = (
  folders: FolderListItem[],
  order: FilesSortOrder,
  account: FileDateAccount
): FolderListItem[] =>
  sortBy(folders, order, account, (folder) => ({
    name: folder.name,
    created: folder.created,
    updated: folder.updated,
    size: folderItemCount(folder)
  }))
